import { type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardFooter } from '~/components/ui/card';
import { FormStepper } from '~/components/ui/form-stepper';

interface StepDef {
  id: string;
  label: string;
  icon?: any;
  [key: string]: any;
}

interface WizardFormLayoutProps {
  title: string;
  subtitle: string;
  savingIndicator?: { saving: boolean; lastSaved: Date | null };
  steps: StepDef[];
  currentStep: number;
  onStepClick: (index: number) => void;
  canProceed: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  onNext: () => void;
  onPrev: () => void;
  submitLabel?: string;
  onSubmit?: () => void;
  submitting?: boolean;
  /** Extra content in the footer between prev/next (e.g. step indicator) */
  footerExtra?: ReactNode;
  /** Override the previous button label */
  prevLabel?: string;
  /** Override the next button label */
  nextLabel?: string;
  children: ReactNode;
}

/**
 * Shared wizard shell: gradient background, header, stepper, card wrapper, navigation footer.
 * Used by both ReenrollmentForm and EnrollmentPacket (and future application form).
 */
export function WizardFormLayout({
  title,
  subtitle,
  savingIndicator,
  steps,
  currentStep,
  onStepClick,
  canProceed,
  isFirstStep,
  isLastStep,
  onNext,
  onPrev,
  submitLabel,
  onSubmit,
  submitting = false,
  footerExtra,
  prevLabel = 'Previous',
  nextLabel = 'Next',
  children,
}: WizardFormLayoutProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
          {savingIndicator?.lastSaved && (
            <p className="text-xs text-muted-foreground mt-1">
              {savingIndicator.saving
                ? 'Saving...'
                : `Last saved: ${savingIndicator.lastSaved.toLocaleTimeString()}`}
            </p>
          )}
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-8">
          <FormStepper
            steps={steps}
            currentStep={currentStep}
            onStepClick={onStepClick}
            allowNavigation={true}
          />
        </div>

        {/* Step Content */}
        <Card>
          <CardContent className="pt-6">
            {children}
          </CardContent>
          <CardFooter className="flex justify-between border-t pt-6">
            <Button
              variant="outline"
              onClick={onPrev}
              disabled={isFirstStep || submitting}
            >
              {prevLabel}
            </Button>

            {footerExtra}

            {isLastStep && onSubmit ? (
              <Button
                onClick={onSubmit}
                disabled={!canProceed || submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  submitLabel || 'Submit'
                )}
              </Button>
            ) : (
              <Button
                onClick={onNext}
                disabled={!canProceed || submitting}
              >
                {savingIndicator?.saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  nextLabel
                )}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
